/**
 * NotificationBell — bell icon + unread badge for the top bar.
 *
 * The unread count comes from a cheap polling query (`/notifications/unread-count`)
 * and is cached separately from the main list so opening the dropdown doesn't
 * need to re-fetch the badge.
 */
import { useState } from 'react'
import { useUnreadNotificationCount } from '../../lib/api/notifications'
import { NotificationDropdown } from './NotificationDropdown'

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const count = useUnreadNotificationCount()
  const unread = count.data?.unread_count ?? 0
  const display = unread > 99 ? '99+' : String(unread)

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications (none unread)'}
        style={bellBtn}
      >
        <BellIcon />
        {unread > 0 ? (
          <span aria-hidden="true" style={badgeStyle}>
            {display}
          </span>
        ) : null}
      </button>

      <NotificationDropdown open={open} onClose={() => setOpen(false)} />
    </div>
  )
}

function BellIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  )
}

const bellBtn: React.CSSProperties = {
  position: 'relative',
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: 'transparent',
  color: '#1f2937',
  border: '1px solid transparent',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const badgeStyle: React.CSSProperties = {
  position: 'absolute',
  top: -2,
  right: -2,
  minWidth: 18,
  height: 18,
  padding: '0 5px',
  borderRadius: 999,
  background: '#dc2626',
  color: '#fff',
  fontSize: 10,
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
}
